import React, { useState } from 'react'
import { Save, X } from 'lucide-react'

function AudioForm({ 
  initialData = {}, 
  onSave, 
  onCancel, 
  isEditing = false 
}) {
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    description: initialData.description || '',
    tags: initialData.tags?.join(', ') || '',
    ...initialData
  })
  
  const [errors, setErrors] = useState({})
  
  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required'
    }
    
    if (formData.title?.length > 100) {
      newErrors.title = 'Title must be 100 characters or less'
    }
    
    if (formData.description?.length > 500) {
      newErrors.description = 'Description must be 500 characters or less'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    const processedData = {
      ...formData,
      title: formData.title.trim(),
      description: formData.description?.trim() || '',
      tags: formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : []
    }
    
    onSave(processedData)
  }
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
          Title *
        </label>
        <input
          type="text"
          id="title"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className={`input-field ${
            errors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
          }`}
          placeholder="Enter a title for your audio file"
          maxLength={100}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          {formData.title.length}/100 characters
        </p>
      </div>
      
      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
          className={`input-field ${
            errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
          }`}
          placeholder="Add a description for your audio file (optional)"
          maxLength={500}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          {(formData.description || '').length}/500 characters
        </p>
      </div>
      
      {/* Tags */}
      <div>
        <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <input
          type="text"
          id="tags"
          value={formData.tags}
          onChange={(e) => handleChange('tags', e.target.value)}
          className="input-field"
          placeholder="Enter tags separated by commas (e.g., speech, announcement, tutorial)"
        />
        <p className="mt-1 text-sm text-gray-500">
          Separate tags with commas to help organize your audio files
        </p>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary flex items-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        
        <button
          type="submit"
          className="btn-primary flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isEditing ? 'Update' : 'Save'} Audio
        </button>
      </div>
    </form>
  )
}

export default AudioForm
